<template>
  <!-- Checkout Options -->
  <div>
    <b-row>
      <b-col class="col-12 col-sm-6 mb-1 mb-sm-0 d-flex">
        <b-button
          v-b-tooltip.hover.bottom.v-primary
          variant="outline-secondary-black"
          class="px-1"
          title="Back to Collection"
          link
          :to="{ name: 'collection/products', params: { collectionId: cartList[0].collectionId }}"
          @click="trackBackButton"
        >
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </b-button>
        <b-button
          variant="secondary-black"
          class="ml-1 d-flex align-items-center"
          @click="onClickBuyingSummary"
        >
          <feather-icon
            icon="MenuIcon"
            size="16"
          />
          <div class="ml-50">
            Buying Summary
          </div>
        </b-button>
      </b-col>
      <b-col class="col-12 col-sm-6 d-flex justify-content-sm-end">
        <b-button
          variant="outline-secondary-black"
          class="mr-1"
          @click="onClickImportCart"
        >
          <import-icon class="align-top" />
          Import
        </b-button>
        <b-button
          class="border-10"
          variant="outline-secondary-black"
          @click="onClickExportMultipleCart"
        >
          <export-icon class="align-top" />
          Export cart for all stores
        </b-button>
      </b-col>
    </b-row>

    <!-- Buying Summary Modal -->
    <template v-if="cartList.length">
      <buying-summary-modal
        :cart-list="cartList"
        :cart-values-list="cartValuesList"
        :total-order-value="totalOrderValue"
        @on-close-modal="onHideBuyingSummaryModal"
      />
      <select-stores-modal
        :modal-id="MULTIPLE_CART_EXPORT_MODAL_ID"
        modal-title="Export cart of all stores"
        ok-btn-text="Export"
        :store-list="cartValuesList"
        :is-downloading="isExporting"
        @on-click-download="onClickExportCart"
        @on-change-stores="(selectedStores) => trackExportCartModal('change-stores', cartList, { exportType, selectedStoreIds: selectedStores.map(store => store.storeId) })"
        @on-close-modal="trackExportCartModal('close-export-cart-of-all-modal', cartList, { exportType })"
      />
    </template>
    <cart-export-modal
      :is-multiple-cart="true"
      @on-close-export-cart-modal="trackExportCartModal('close', cartList)"
      @on-change-format="(newExportType) => trackExportCartModal('format-change', cartList, { newExportType })"
      @on-click-export-modal-download="(newExportType) => trackExportCartModal('download-click', cartList, { newExportType })"
      @open-export-multiple-carts-modal="openExportCartModal"
    />
  </div>
</template>

<script>
import ExportIcon from '@/@core/assets/svg-components/ExportIcon.vue'
import ImportIcon from '@/@core/assets/svg-components/ImportIcon.vue'
import {
  BButton, BCol, BRow, VBTooltip, VBModal,
} from 'bootstrap-vue'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import constants, { CART_EXPORT_MODAL_ID, DOWNLOAD_TYPES, MULTIPLE_CART_EXPORT_MODAL_ID } from '@/constants'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { EXPORT_CART_MULTIPLE } from '@/store/modules/shop.module'
import analytics from '@/@core/utils/analytics'
import { UPDATE_CARTS } from '@/store/modules/checkout-v2.module'
import NotificationMixin from '@/views/mixins/NotificationMixin.vue'
import SelectStoresModal from './components/SelectStoresModal.vue'
import BuyingSummaryModal from './BuyingSummaryModal.vue'
import CartExportModal from '../checkout/CartExportModal.vue'
import { trackExportCartModal } from './trackMultipleStoreCartHelpers'

const cartErrorMessage = cart => (cart?.storeName ? `${cart.storeName} - ${cart.error}` : cart?.error)

function toastCartErrors(err) {
  const failedCarts = err?.response?.data?.data?.failedCarts ?? []
  if (!failedCarts.length) return apiToastError(err)
  failedCarts.forEach(cart => apiToastError(cartErrorMessage(cart)))
}

const { CURRENCY_CONVERSIONS } = c

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'CartActions',
  components: {
    BRow,
    BCol,
    BButton,
    ImportIcon,
    ExportIcon,
    BuyingSummaryModal,
    SelectStoresModal,
    CartExportModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-modal': VBModal,
  },
  mixins: [NotificationMixin],
  props: {
    cartList: {
      type: Array,
      required: true,
    },
    cartValuesList: {
      type: Array,
      required: true,
    },
    totalOrderValue: {
      type: Object,
      default: () => ({
        totalCartsValue: 0,
        totalCartsUnits: 0,
        totalCartsRRP: 0,
        currency: CURRENCY_CONVERSIONS.USD.SYMBOL,
      }),
    },
  },
  data() {
    return {
      isExporting: false,
      exportType: '',
      selectedOptions: [],
      MULTIPLE_CART_EXPORT_MODAL_ID,
      trackExportCartModal,
    }
  },
  methods: {
    trackBackButton() {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_BACK_BUTTON, { carts: this.cartList })
    },
    onClickBuyingSummary() {
      this.$bvModal.show('buying_summary_v_2')
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_BUYING_SUMMARY_BUTTON, { cartValuesList: this.cartValuesList })
    },
    onHideBuyingSummaryModal() {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLOSES_BUYING_SUMMARY_MODAL, { carts: this.cartList })
    },
    onClickImportCart() {
      this.$bvModal.show('import_cart_modal')
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_IMPORT_CART_BUTTON)
    },
    onClickExportMultipleCart() {
      this.$bvModal.show(CART_EXPORT_MODAL_ID)
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_EXPORT_CART_BUTTON)
    },
    openExportCartModal({ exportType, selectedOptions }) {
      this.exportType = exportType
      this.selectedOptions = selectedOptions
      this.$bvModal.show(MULTIPLE_CART_EXPORT_MODAL_ID)
      trackExportCartModal('view-export-cart-of-all-modal', this.cartList, { exportType })
    },
    async onClickExportCart(selectedStores = []) {
      const selectedStoreIds = selectedStores.map(store => store.storeId)
      trackExportCartModal('export-all-download-click', this.cartList, { exportType: this.exportType, selectedStoreIds })
      const updated = await this.saveCartsBeforeExport()
      if (!updated) return
      this.dispatchExport(selectedStoreIds)
    },
    async saveCartsBeforeExport() {
      try {
        await this.$store.dispatch(UPDATE_CARTS, { params: { isUpdateFromCartPage: true, skipMinQtyValidation: true } })
        return true
      } catch (err) {
        toastCartErrors(err)
        return false
      }
    },
    dispatchExport(selectedStoreIds) {
      this.isExporting = true
      const payload = {
        downloadType: DOWNLOAD_TYPES.CART_EXPORT.VALUE,
        fileType: this.exportType,
        respectiveIds: selectedStoreIds,
        optionalFields: this.selectedOptions,
      }
      this.$store.dispatch(EXPORT_CART_MULTIPLE, { payload }).then(res => {
        this.$bvModal.hide(MULTIPLE_CART_EXPORT_MODAL_ID)
        apiToastSuccess(res.data.message)
        analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_EXPORT_CART_SUCCESSFULLY, { payload })
        this.notifyDownloadsNavMenu()
      })
      .catch(err => {
        apiToastError(err)
        analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_EXPORT_CART_ERROR, err?.response)
      })
      .finally(() => { this.isExporting = false })
    },
  },
}
</script>
